import { GripHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { type PaletteAnchor, usePaletteTrackSlot } from "./PaletteTrack.tsx";
import {
  COLOR_BORDER,
  COLOR_HEADER_BG,
  COLOR_TEXT_FAINT,
  RADIUS_HEADER,
  useChromeDim,
  Z_TOOLBAR,
} from "./tokens.ts";

export const EDGE_PALETTE_WIDTH = 44;
export const EDGE_PALETTE_MIN_TOP = 52;
export const EDGE_PALETTE_GRIP_HEIGHT = 20;
export const EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT = EDGE_PALETTE_GRIP_HEIGHT + 48;

/**
 * Edge-docked palette shell. Participates in a `PaletteTrack` so sibling
 * palettes on the same edge never overlap.
 *
 * The shell is the absolutely-positioned scrollable container; inside sits an
 * unconstrained measure-wrapper whose natural height is observed by the track
 * (measuring the shell directly would just report the clamped maxHeight).
 */
export function EdgePalette({
  id,
  order,
  anchor,
  storageKey,
  defaultOffsetPx = 0,
  children,
  width = EDGE_PALETTE_WIDTH,
}: {
  id: string;
  order: number;
  anchor: PaletteAnchor;
  storageKey: string;
  defaultOffsetPx?: number;
  children: ReactNode;
  width?: number;
}) {
  const chromeDim = useChromeDim();
  const slot = usePaletteTrackSlot({
    id,
    order,
    anchor,
    storageKey,
    defaultOffsetPx,
    minScrollablePx: EDGE_PALETTE_MIN_SCROLLABLE_HEIGHT,
  });

  return (
    <div
      data-no-ctx
      className="hidden-scrollbar"
      style={{
        position: "absolute",
        top: slot.top,
        left: 0,
        width,
        zIndex: Z_TOOLBAR,
        pointerEvents: "auto",
        background: COLOR_HEADER_BG,
        borderRight: `0.5px solid ${COLOR_BORDER}`,
        borderTop: `0.5px solid ${COLOR_BORDER}`,
        borderBottom: `0.5px solid ${COLOR_BORDER}`,
        borderTopRightRadius: RADIUS_HEADER,
        borderBottomRightRadius: RADIUS_HEADER,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        maxHeight: slot.maxHeight,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarWidth: "none",
        ...chromeDim,
      }}
    >
      <div
        ref={slot.measureRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div
          onPointerDown={slot.onGripPointerDown}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: EDGE_PALETTE_GRIP_HEIGHT + 8,
            cursor: "ns-resize",
            flexShrink: 0,
            color: COLOR_TEXT_FAINT,
            touchAction: "none",
          }}
        >
          <GripHorizontal size={14} />
        </div>
        {children}
      </div>
    </div>
  );
}
